import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Pause, Play } from "lucide-react";
import { homepageTrips } from "@/data/homepageTrips";
import { destinationImages } from "@/data/destinationImages";
import "@/components/HomeRules.css";

export function formatPoDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return "";
    const date = new Date(value + "T12:00:00Z");
    return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? "" : new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" }).format(date);
}
export function poStatus(status) {
    if (["CLOSE", "CLOSED"].includes(status)) return { label: "PO DITUTUP", kind: "closed" };
    if (["OPEN", "AKTIF", "BERJALAN"].includes(status)) return { label: "PO DIBUKA", kind: "open" };
    if (status === "PLANNED") return { label: "PLANNED", kind: "planned" };
    return { label: "STATUS BELUM TERSEDIA", kind: "unknown" };
}
function PoDetails({ trip, controls }) {
    const status = poStatus(trip.status);
    const country = (trip.country || "").trim().toUpperCase();
    const photo = destinationImages[country];
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [country]);
    const start = formatPoDate(trip.start_date), end = formatPoDate(trip.end_date);
    const sameYear = trip.start_date?.slice(0, 4) === trip.end_date?.slice(0, 4);
    const period = start && end ? `${sameYear ? start.replace(/ \d{4}$/, "") : start} – ${end}` : "";
    const name = /^PO\b/i.test(trip.name) ? trip.name : `PO ${trip.name}`;
    const location = photo?.destination || trip.name;
    const flag = photo?.flag || "";
    const label = status.kind === "open" ? "OPEN PO" : status.label;
    return <div data-testid="live-shopping-card">
        <article className="wx-card overflow-hidden" data-testid="visible-po-card" aria-label={name}>
            <span className="sr-only">{name}</span>
            <div data-testid="hero-image-wrap" className="relative bg-[color:var(--wx-surface)] aspect-[6/5]">
                {photo && !failed ? <img key={country} src={photo.src} alt={photo.alt} width="1200" height="900" className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" onError={() => setFailed(true)} /> : <div className="absolute inset-0 wx-po-photo-fallback"><MapPin size={22} aria-hidden="true" /><span>{trip.country || "Info perjalanan PO"}</span></div>}
                {controls}
            </div>
            <div className="px-5 py-[7px] sm:px-6 sm:py-3">
                <span className="flex items-center gap-2 text-[11px] leading-[16px] font-bold tracking-[0.22em] uppercase text-[color:var(--wx-ink)]">
                    {status.kind === "open" && <span data-testid="live-dot" className="wx-live-dot" aria-hidden="true" />}
                    {label} / LIVE SHOPPING
                </span>
                <p data-testid="po-destination" className="mt-[2px] text-xl sm:text-2xl font-semibold text-[color:var(--wx-ink)] leading-tight">{flag} {location}</p>
                <p data-testid="po-dates" className="text-sm leading-5 text-[color:var(--wx-muted)]" style={{minHeight:20}}>{period}</p>
                <p className="text-[11px] leading-normal text-[color:var(--wx-muted)]" title="Estimasi pengiriman bertahap, bukan jaminan tiba di alamat Cici.">ETA: {trip.estimate || "Menunggu pembaruan jadwal"}</p>
            </div>
        </article>
    </div>;
}
export function PoCarousel({ trips }) {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const touchStart = useRef(null);
    const count = trips.length;
    useEffect(() => {
        const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!query) return;
        const update = () => setReducedMotion(query.matches);
        update(); query.addEventListener?.("change", update);
        return () => query.removeEventListener?.("change", update);
    }, []);
    useEffect(() => { setIndex(0); }, [trips]);
    const rotating = count > 1 && !paused && !hovered && !focused && !reducedMotion;
    useEffect(() => {
        if (!rotating) return;
        const timer = setInterval(() => setIndex(value => (value + 1) % count), 5000);
        return () => clearInterval(timer);
    }, [rotating, count]);
    const move = (value) => { setPaused(true); setIndex((value + count) % count); };
    if (!count) return <div className="wx-card wx-po-loading"><p>Informasi PO akan diperbarui di sini.</p></div>;
    const selected = Math.min(index, count - 1);
    return <section className="wx-po-carousel" aria-label="Informasi PO" aria-roledescription={count > 1 ? "carousel" : undefined}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
        onKeyDown={(event) => { if (count < 2) return; if (event.key === "ArrowRight") { event.preventDefault(); move(selected + 1); } if (event.key === "ArrowLeft") { event.preventDefault(); move(selected - 1); } }}
        onTouchStart={(event) => { touchStart.current = {x:event.touches[0].clientX,y:event.touches[0].clientY}; setPaused(true); }}
        onTouchEnd={(event) => { const start=touchStart.current; touchStart.current=null; if (!start || count < 2) return; const dx=event.changedTouches[0].clientX-start.x,dy=event.changedTouches[0].clientY-start.y; if (Math.abs(dx)>50 && Math.abs(dx)>Math.abs(dy)) move(selected+(dx<0?1:-1)); }}>
        <div aria-live={rotating ? "off" : "polite"} aria-atomic="true"><PoDetails trip={trips[selected]} controls={count > 1 && <div className="wx-po-carousel-controls" data-testid="po-carousel-controls">
            <button type="button" onClick={() => move(selected - 1)} aria-label="PO sebelumnya"><ChevronLeft size={18}/></button>
            <div className="wx-po-carousel-dots" aria-label="Pilih PO">{trips.map((trip, position) => <button type="button" key={`${trip.name}-${position}`} onClick={() => move(position)} aria-label={`Tampilkan PO ${trip.name}`} aria-current={position === selected ? "true" : undefined}><span/></button>)}</div>
            <span className="wx-po-carousel-count">{selected + 1} / {count}</span>
            {!reducedMotion && <button type="button" onClick={() => setPaused(value=>!value)} aria-label={paused ? "Jalankan pergantian otomatis" : "Jeda pergantian otomatis"}>{paused ? <Play size={15}/> : <Pause size={15}/>}</button>}
            <button type="button" onClick={() => move(selected + 1)} aria-label="PO berikutnya"><ChevronRight size={18}/></button>
        </div>} /></div>

    </section>;
}
export default function Hero() {
    return <section data-testid="hero-section" className="wx-container pt-10 sm:pt-14 lg:pt-16 wx-fade-up">
        <div className="grid gap-8 md:gap-10 lg:gap-14 md:grid-cols-2 md:items-center">
                {/* LEFT — big elegant title */}
                <div data-testid="hero-title">
                    <h1 className="font-serif leading-[1.05] tracking-tight text-[color:var(--wx-ink)]">
                        <span className="block text-4xl sm:text-5xl lg:text-6xl font-medium whitespace-nowrap">
                            WxFaous
                        </span>
                        <span className="block italic font-light wx-gold text-[1.6rem] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 tracking-normal">
                            Jastip & Live Shopping
                        </span>
                        <span className="block text-[1.6rem] sm:text-3xl lg:text-4xl mt-2 font-medium">
                            Luar Negeri.
                        </span>
                    </h1>
                </div>

            <div className="min-w-0">
                <PoCarousel trips={homepageTrips} />
            </div>
        </div>
    </section>;
}
